import React from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import type { SessionQuestion, SessionQuestionType } from '../../../types/aai';
import type { DraftBody } from '../../../services/partnerSessionsApi';
import { Input, Select } from '../../../components/aa';
import { TYPE_LABEL } from './sessionShared';

/*
 * The session draft the agent writes and the partner reviews: title, the
 * line the recipients read, and every question. Whether it is a
 * questionnaire or a survey follows from what the partner asked for, so the
 * agent sets it and the partner never has to. Each edit goes up
 * through onChange; the studio saves it, and the agent reads it next turn.
 */

const TYPES: SessionQuestionType[] = ['text', 'single', 'multi', 'scale', 'tools', 'file', 'secret'];

/* A key goes to one project's Vault, so the key type is offered only on a
   project session; a question already of that type keeps it so it can be changed. */
const typesFor = (forProject: boolean, current: SessionQuestionType): SessionQuestionType[] => {
  if (forProject || current === 'secret') return TYPES;
  return TYPES.filter((t) => t !== 'secret');
};

const envName = (raw: string): string => raw.toUpperCase().replace(/[^A-Z0-9_]/g, '_').slice(0, 128);

const newId = (): string => Math.random().toString(36).slice(2, 14);

const withType = (q: SessionQuestion, type: SessionQuestionType): SessionQuestion => {
  const next: SessionQuestion = { id: q.id, label: q.label, type };
  if (type === 'single' || type === 'multi') {
    let options = q.options || [];
    if (options.length < 2) options = ['', ''];
    next.options = options;
  }
  if (type === 'scale') {
    next.low = q.low || 'Not at all';
    next.high = q.high || 'Completely';
  }
  if (type === 'secret') next.env_name = q.env_name || '';
  return next;
};

const IconButton: React.FC<{ label: string; disabled?: boolean; onClick: () => void; children: React.ReactNode }> = ({ label, disabled, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className="flex h-8 w-8 items-center justify-center rounded-[8px] text-ink-dim transition-colors hover:bg-canvas hover:text-ink disabled:opacity-30"
  >
    {children}
  </button>
);

const QuestionCard: React.FC<{
  question: SessionQuestion;
  number: number;
  first: boolean;
  last: boolean;
  disabled: boolean;
  forProject: boolean;
  keyNames: string[];
  onChange: (q: SessionQuestion) => void;
  onMove: (direction: -1 | 1) => void;
  onDelete: () => void;
}> = ({ question: q, number, first, last, disabled, forProject, keyNames, onChange, onMove, onDelete }) => {
  const options = q.options || [];
  let marker = 'rounded-[4px]';
  if (q.type === 'single') marker = 'rounded-full';
  const setOption = (i: number, value: string) => onChange({ ...q, options: options.map((o, n) => {
    if (n === i) return value;
    return o;
  }) });
  return (
    <div className="rounded-[14px] border border-line bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="mt-1.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-canvas-inset font-mono text-[12px] font-bold text-ink">{number}</span>
        <div className="min-w-0 flex-1 space-y-2.5">
          <textarea
            value={q.label}
            disabled={disabled}
            rows={2}
            onChange={(e) => onChange({ ...q, label: e.target.value })}
            placeholder="Write the question"
            aria-label={`Question ${number}`}
            className="block w-full resize-none rounded-[10px] border border-line bg-white px-3 py-2 text-[14px] font-semibold leading-[20px] text-ink outline-none focus:border-ink focus:ring-[3px] focus:ring-ink/10"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={q.type}
              disabled={disabled}
              onChange={(e) => onChange(withType(q, e.target.value as SessionQuestionType))}
              aria-label={`Answer type for question ${number}`}
              className="!h-9 !w-auto !text-[12.5px]"
            >
              {typesFor(forProject, q.type).map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </Select>
          </div>
          {(q.type === 'single' || q.type === 'multi') && (
            <div className="space-y-1.5">
              {options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`h-3.5 w-3.5 shrink-0 border border-line-strong ${marker}`} aria-hidden />
                  <Input value={o} disabled={disabled} onChange={(e) => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`} aria-label={`Option ${i + 1}`} className="!h-9 !text-[13px]" />
                  <IconButton label={`Remove option ${i + 1}`} disabled={disabled || options.length <= 2} onClick={() => onChange({ ...q, options: options.filter((_, n) => n !== i) })}>
                    <X size={14} />
                  </IconButton>
                </div>
              ))}
              {options.length < 12 && !disabled && (
                <button type="button" onClick={() => onChange({ ...q, options: [...options, ''] })} className="inline-flex items-center gap-1 text-[12px] font-semibold text-ink-muted hover:text-ink">
                  <Plus size={13} /> Add option
                </button>
              )}
            </div>
          )}
          {q.type === 'tools' && (
            <p className="m-0 rounded-[10px] bg-canvas-inset px-3 py-2 text-[12px] text-ink-muted">
              People answer by tapping app logos, with search across every app we support.
            </p>
          )}
          {q.type === 'file' && (
            <p className="m-0 rounded-[10px] bg-canvas-inset px-3 py-2 text-[12px] text-ink-muted">
              People answer by uploading up to 5 documents: PDF, Word, Excel, CSV, text or images, up to 25 MB each.
            </p>
          )}
          {q.type === 'secret' && (
            <div className="space-y-1.5">
              <Input
                value={q.env_name || ''}
                disabled={disabled}
                list={`keys-${q.id}`}
                onChange={(e) => onChange({ ...q, env_name: envName(e.target.value) })}
                placeholder="Variable name, like STRIPE_SECRET_KEY"
                aria-label={`Variable name for question ${number}`}
                className="!h-9 !font-mono !text-[12.5px]"
              />
              <datalist id={`keys-${q.id}`}>
                {keyNames.map((k) => <option key={k} value={k} />)}
              </datalist>
              <p className="m-0 rounded-[10px] bg-canvas-inset px-3 py-2 text-[12px] text-ink-muted">
                The answer goes straight to this project's Vault. The Planner checks it before it is set. You only ever see the first few characters.
              </p>
              {!forProject && <p className="m-0 text-[12px] text-danger">A key question needs the session to be for a project.</p>}
            </div>
          )}
          {q.type === 'scale' && (
            <div className="grid gap-2 sm:grid-cols-2">
              <Input value={q.low || ''} disabled={disabled} onChange={(e) => onChange({ ...q, low: e.target.value })} placeholder="1 means…" aria-label="Label for 1" className="!h-9 !text-[13px]" />
              <Input value={q.high || ''} disabled={disabled} onChange={(e) => onChange({ ...q, high: e.target.value })} placeholder="5 means…" aria-label="Label for 5" className="!h-9 !text-[13px]" />
            </div>
          )}
        </div>
        {!disabled && (
          <div className="flex shrink-0 flex-col">
            <IconButton label={`Move question ${number} up`} disabled={first} onClick={() => onMove(-1)}><ArrowUp size={14} /></IconButton>
            <IconButton label={`Move question ${number} down`} disabled={last} onClick={() => onMove(1)}><ArrowDown size={14} /></IconButton>
            <IconButton label={`Delete question ${number}`} onClick={onDelete}><Trash2 size={14} /></IconButton>
          </div>
        )}
      </div>
    </div>
  );
};

export const DraftEditor: React.FC<{
  draft: DraftBody;
  disabled: boolean;
  keyNames?: string[];
  onChange: (next: DraftBody) => void;
}> = ({ draft, disabled, keyNames = [], onChange }) => {
  const setQuestion = (i: number, q: SessionQuestion) => onChange({ ...draft, questions: draft.questions.map((x, n) => {
    if (n === i) return q;
    return x;
  }) });
  const move = (i: number, direction: -1 | 1) => {
    const next = [...draft.questions];
    const j = i + direction;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ ...draft, questions: next });
  };
  const add = () => onChange({ ...draft, questions: [...draft.questions, { id: newId(), label: '', type: 'text' }] });

  return (
    <div className="space-y-4">
      <div className="space-y-2.5">
        <input
          value={draft.title}
          disabled={disabled}
          onChange={(e) => onChange({ ...draft, title: e.target.value })}
          placeholder="Session title"
          aria-label="Session title"
          className="block w-full border-0 bg-transparent p-0 text-[20px] font-bold leading-[26px] text-ink outline-none placeholder:text-ink-soft"
        />
        <textarea
          value={draft.description}
          disabled={disabled}
          rows={2}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
          placeholder="One or two sentences the people you send it to read first: why you are asking."
          aria-label="What the recipients read first"
          className="block w-full resize-none rounded-[10px] border border-line bg-white px-3 py-2 text-[13px] leading-[19px] text-ink-muted outline-none focus:border-ink focus:ring-[3px] focus:ring-ink/10"
        />
      </div>

      {draft.questions.length === 0 && (
        <div className="rounded-[14px] border border-dashed border-line-strong bg-white px-5 py-8 text-center">
          <strong className="block text-[14px] text-ink">No questions yet</strong>
          <small className="mt-1 block text-[12.5px] text-ink-muted">Tell the agent what you want to learn, upload a document, or add questions yourself.</small>
        </div>
      )}
      <div className="space-y-2.5">
        {draft.questions.map((q, i) => (
          <QuestionCard
            key={q.id}
            question={q}
            number={i + 1}
            first={i === 0}
            last={i === draft.questions.length - 1}
            disabled={disabled}
            forProject={draft.purpose === 'project'}
            keyNames={keyNames}
            onChange={(next) => setQuestion(i, next)}
            onMove={(d) => move(i, d)}
            onDelete={() => onChange({ ...draft, questions: draft.questions.filter((_, n) => n !== i) })}
          />
        ))}
      </div>
      {!disabled && draft.questions.length < 30 && (
        <button type="button" onClick={add} className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-white px-3.5 py-2 text-[12.5px] font-semibold text-ink hover:border-ink whitespace-nowrap">
          <Plus size={14} /> Add a question
        </button>
      )}
    </div>
  );
};

export default DraftEditor;
